import { isType, variant, fields } from 'variant';
import type { VariantOf } from 'variant';
import {
  NodeClient,
  Scope,
  getDefaultIntegrations,
  defaultStackParser,
  makeNodeTransport,
} from '@sentry/node';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { isTelemetryEnabledSync } from '@wix/cli-user-config';
import { extractHttpError, getHttpErrorInfo } from '@wix/cli-http-client';
import VError from 'verror';
import { outdent } from 'outdent';
import type { CliError } from '@wix/cli-error';
import {
  isCliSystemError,
  normalizeError,
  CliSystemErrorCode,
} from '@wix/cli-error';
import EventEmitter from 'node:events';
import { createEnv } from '@wix/cli-env';
import {
  truncateStringValues,
  toSafeJson,
  normalizeRequestId,
} from './error-utils.js';

interface TypedEventEmitter {
  on: (event: 'errorReported', listener: (error: CliError) => void) => this;
  emit: (event: 'errorReported', error: CliError) => boolean;
}

export interface ErrorReporterOptions {
  dsn: string;
  release: string;
}

interface User {
  id: string;
}

interface UserFeedback {
  userId: string;
  eventId: string;
  feedback: string;
}

export type ErrorReporter = ReturnType<typeof createErrorReporter>;

export const ReportErrorResult = variant({
  Reported: fields<{ eventId: string }>(),
  Ignored: {},
});
export type ReportErrorResult = VariantOf<typeof ReportErrorResult>;

const reportError = (
  scope: Scope,
  originalError: CliError,
  { data }: { data?: unknown } = {}
) => {
  if (!isCliSystemError(originalError)) {
    return ReportErrorResult.Ignored();
  }

  const error = isType(originalError.cliCode, CliSystemErrorCode.UnknownFailure)
    ? originalError.cliCode.error
    : originalError;

  const eventId = scope.captureException(error, {
    data,
    captureContext: (localScope) => {
      const httpError = extractHttpError(error);

      if (httpError) {
        localScope.setTag(
          'request.id',
          normalizeRequestId(httpError.requestId)
        );

        const errorInfo = getHttpErrorInfo(httpError);

        errorInfo.request.data = toSafeJson(errorInfo.request.data);
        errorInfo.response.data = toSafeJson(errorInfo.response.data);

        const truncatedErrorInfo = truncateStringValues(errorInfo, 200);

        localScope.setContext('Request Info', truncatedErrorInfo);
      }

      if (error instanceof Error) {
        localScope.setContext('Error Details', {
          Info: VError.info(error),
          'Full Stack': VError.fullStack(error),
        });
      }

      const { isCI, isPR, ciName, cliEnvName, cliTrackingId } = createEnv();

      localScope.setTag('ci', yesOrNo(isCI));
      localScope.setTag('ci.name', ciName);
      localScope.setTag('cli.env_name', cliEnvName);
      localScope.setTag('cli.tracking_id', cliTrackingId);

      localScope.setContext('CI', {
        Name: ciName,
        'Is CI': yesOrNo(isCI),
        'Is PR': yesOrNo(isPR),
      });

      return localScope;
    },
  });

  return ReportErrorResult.Reported({ eventId });
};

export function createErrorReporter({ dsn, release }: ErrorReporterOptions) {
  const { sentryDsn: sentryDsnOverride } = getTestOverrides();

  // Error reports are telemetry too: `wix telemetry off` and `DO_NOT_TRACK`
  // must silence Sentry the same way they silence BI and Panorama.
  const enabled =
    isTelemetryEnabledSafely() &&
    (sentryDsnOverride !== undefined || process.env.NODE_ENV === 'production');

  // https://docs.sentry.io/platforms/javascript/best-practices/multiple-sentry-instances/
  const sentryClient = new NodeClient({
    enabled,
    dsn: sentryDsnOverride ?? dsn,
    release,
    integrations: getDefaultIntegrations({}).filter(
      (integration) =>
        integration.name !== 'OnUnhandledRejection' &&
        integration.name !== 'OnUncaughtException' &&
        // By default, @sentry/node adds metadata about installed packages.
        integration.name !== 'Modules'
    ),
    stackParser: defaultStackParser,
    transport: makeNodeTransport,
    beforeSend(event) {
      // By default @sentry/node includes the local hostname as `server_name`
      // which is sensitive and not useful.

      delete event.server_name;
      return event;
    },
    // Since we pass `requestInfo`, the default depth of 3
    // redacts most useful info passed from API error responses
    // so we increase it to include more of the potential error http response.
    normalizeDepth: 8,
    normalizeMaxBreadth: 20,
  });

  const sentryScope = new Scope();
  sentryScope.setClient(sentryClient);
  sentryClient.init();

  const emitter: TypedEventEmitter = new EventEmitter();

  return {
    on: emitter.on.bind(emitter),
    reportError: (
      originalError: unknown,
      { data }: { data?: unknown } = {}
    ) => {
      const error = normalizeError(originalError);
      // Short-circuit rather than rely on the disabled client dropping the
      // event, so callers (e.g. the user feedback prompt) see `Ignored`
      // instead of an event id that never reached Sentry.
      const result = enabled
        ? reportError(sentryScope, error, { data })
        : ReportErrorResult.Ignored();

      emitter.emit('errorReported', error);

      return result;
    },
    reportUserFeedback: async ({ userId, eventId, feedback }: UserFeedback) => {
      /**
       * Importing @sentry/browser because it includes the `captureUserFeedback` method
       * which is not available in @sentry/node.
       * This is a workaround until the method is added to @sentry/node.
       * See: https://github.com/getsentry/sentry-javascript/issues/11072
       */
      const { BrowserClient } = await import('@sentry/browser');

      const client = new BrowserClient({
        enabled: sentryClient.getOptions().enabled,
        dsn: sentryClient.getOptions().dsn,
        integrations: [],
        transport: makeNodeTransport,
        stackParser: defaultStackParser,
      });

      // eslint-disable-next-line @typescript-eslint/no-deprecated
      client.captureUserFeedback({
        event_id: eventId,
        name: '',
        email: `${userId}@wix.com`,
        comments: outdent`
          ---
          user: https://bo.wix.com/um/users/${userId}
          ---
          ${feedback}
        `,
      });
    },
    setContext: (name: string, context: Record<string, unknown>) =>
      sentryScope.setContext(name, context),
    setTag: (key: string, value: string) => sentryScope.setTag(key, value),
    setCliSessionId: (cliSessionId: string) =>
      sentryScope.setTag('cli.session_id', cliSessionId),
    setAiAgentName: (aiAgentName: string) => {
      sentryScope.setTag('aiagent', 'yes');
      sentryScope.setTag('aiagent.name', aiAgentName);
    },
    setUser: (user: User) => sentryScope.setUser(user),
    getUser: (): User | undefined => {
      const user = sentryScope.getUser();
      if (typeof user?.id === 'string') {
        return { id: user.id };
      }
    },
  };
}

/**
 * The reporter is created before any error handling is installed, so a
 * malformed or half-written `user.config.json` must not crash the CLI here.
 * Fail closed: an unreadable config may well contain an opt-out we can't
 * see, and the config error itself is reported to the user with a proper
 * message once the user config is read again later on.
 */
function isTelemetryEnabledSafely() {
  try {
    return isTelemetryEnabledSync();
  } catch {
    return false;
  }
}

function yesOrNo(value: boolean | null) {
  if (value == null) {
    return null;
  }
  return value ? 'yes' : 'no';
}
